import React, { useMemo } from "react";
import { get } from "lodash";
import { RunnerBI } from "../../../../hooks/useGetRunnerFromBI";
import { CompleteRightInfoType } from "./types";
import {
  Entry,
  LeftSideContainer,
  RightSideContainer,
  InfoContainer,
  AdvancedTextContainer,
  Info
} from "./styled-components";
import { leftInfo } from "./advancedInfo";
import {
  computeLeftInfo,
  computePercentageOrValue,
  isTopStat
} from "../../computeInfo";
import { BestPerformanceStats } from "../../../RunnerList/types";

interface AdvancedInfoProps {
  runnerInfo: RunnerBI;
  isScratched?: boolean;
  isWPSBetType: boolean;
  isInlineHandicapping?: boolean;
  bestPerformanceRunners?: BestPerformanceStats;
  isDesktop?: boolean;
  hasSilk: boolean;
  hasMobileOrder?: boolean;
}

export const getFeaturesFromTitle = (
  title: string,
  hasDescription?: boolean
) => ({
  isDam: title.includes("dam") && hasDescription,
  isSire: title.includes("sire") && hasDescription,
  isGrandDam: title.includes("grandDam"),
  hasGrandDamDescription: title.includes("grandDam") && hasDescription
});

const AdvancedInfo = ({
  runnerInfo,
  isScratched,
  isWPSBetType,
  isInlineHandicapping,
  bestPerformanceRunners,
  isDesktop = true,
  hasSilk,
  hasMobileOrder = true
}: AdvancedInfoProps) => {
  const renderInfoLeftSide = useMemo(() => {
    const getLeftInfo = Object.entries(runnerInfo).filter((info) =>
      leftInfo.includes(info[0])
    );

    return (
      <LeftSideContainer
        data-qa-label="advanced-info-left-side"
        isWPSBetType={isWPSBetType}
      >
        {getLeftInfo.map(([title, description]) => {
          const hasDescription = !!description;

          const { isDam, isSire, isGrandDam, hasGrandDamDescription } =
            getFeaturesFromTitle(title, hasDescription);

          return (
            <Entry
              data-qa-label={`runner-${title}`}
              isDam={isDam}
              isSire={isSire}
              isGrandDam={isGrandDam}
              hasGrandDamDescription={hasGrandDamDescription}
              key={title}
            >
              {computeLeftInfo(title, description)}
            </Entry>
          );
        })}
      </LeftSideContainer>
    );
  }, [runnerInfo]);

  const renderInfoRightSide = useMemo(() => {
    const {
      numberOfStarts,
      winPercentage,
      top3WinPercentage,
      powerRating,
      daysOff
    } = runnerInfo;

    const extraRightInfo = isDesktop
      ? {
          powerRating: { value: powerRating, description: "PWR RTG" },
          daysOff: { value: daysOff, description: "DAYS OFF" }
        }
      : {};

    const completeRightInfo = {
      numberOfStarts: { value: numberOfStarts, description: "STARTS" },
      winPercentage: { value: winPercentage, description: "WIN%" },
      top3Percentage: { value: top3WinPercentage, description: "TOP3%" },
      ...extraRightInfo
    };

    return (
      <RightSideContainer data-qa-label="right-side-container">
        {Object.keys(completeRightInfo).map((key) => {
          const rightInfo =
            completeRightInfo[key as keyof CompleteRightInfoType];
          const topStat = get(bestPerformanceRunners, key, 0);

          return (
            <InfoContainer
              data-qa-label={`advanced-righ-info-${key}`}
              key={key}
            >
              <Info
                isValue
                data-qa-label={`${key}-value`}
                isTopStat={isTopStat(rightInfo?.value, topStat)}
              >
                {computePercentageOrValue(rightInfo?.value, key)}
              </Info>
              <Info data-qa-label={`${key}-description`} isDescription>
                {rightInfo?.description}
              </Info>
            </InfoContainer>
          );
        })}
      </RightSideContainer>
    );
  }, [runnerInfo]);

  return (
    <AdvancedTextContainer
      data-qa-label="advanced-info-container"
      isScratched={isScratched}
      isInlineHandicapping={isInlineHandicapping}
      hasSilk={hasSilk}
      hasMobileOrder={hasMobileOrder}
    >
      {renderInfoLeftSide}
      {renderInfoRightSide}
    </AdvancedTextContainer>
  );
};

export default AdvancedInfo;
